import { compareAsc, isBefore, isToday, startOfDay, subDays } from 'date-fns';
import { catchErrorSync } from '../app/components/Common/AppErrorBoundary';
import i18n from '../app/locales/i18n';
import Helper from '../app/modules/Helper';
import store from '../store';
import { getFbClientId } from '../utils/cookies';
import {
	emitGMTEvent,
	hideAndShowErrors,
	sendDataToCompare,
	sendDataToPassthruForm,
	setTicketData,
	setTooltipError,
	submitForm,
	togglePriceAlertSignUpModal,
	toggleProgressLoading,
} from './baseActions';
import { saveFrontFormDataToSessionStorage } from './baseActions.utils';
import { setErrorForPriceAlertEmail, setErrorForPriceAlertPhone } from './error-checks';
import {
	CAR_SELECT_FROM_DATE,
	CAR_SELECT_FROM_LOCATION,
	CAR_SELECT_FROM_TIME,
	CAR_SELECT_TO_DATE,
	CAR_SELECT_TO_LOCATION,
	CAR_SELECT_TO_TIME,
	CAR_TOGGLE_RETURN_LOCATION,
} from './types';

export const selectFromDate = (date) =>
	catchErrorSync((dispatch) => {
		if (!Helper.isValidDate(date) || isBefore(date, subDays(new Date(), 1))) {
			return;
		}
		if (date.getTime() > store.getState().car.selectedToDate.getTime()) {
			store.dispatch(selectToDate(date));
		}
		dispatch({
			type: CAR_SELECT_FROM_DATE,
			payload: date,
		});

		const errors = store.getState().base.tooltipErrors;
		delete errors['date-from'];
		store.dispatch(setTooltipError(errors));
	});

export const selectToDate = (date) =>
	catchErrorSync((dispatch) => {
		if (!Helper.isValidDate(date) || isBefore(date, subDays(new Date(), 1))) {
			return;
		}
		dispatch({
			type: CAR_SELECT_TO_DATE,
			payload: date,
		});
		const errors = store.getState().base.tooltipErrors;
		delete errors['date-to'];
		store.dispatch(setTooltipError(errors));
	});

export const selectFromTime = (time) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: CAR_SELECT_FROM_TIME,
			payload: time,
		});

		const errors = store.getState().base.tooltipErrors;
		delete errors['date-from'];
		store.dispatch(setTooltipError(errors));
	});

export const selectToTime = (time) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: CAR_SELECT_TO_TIME,
			payload: time,
		});
		const errors = store.getState().base.tooltipErrors;
		delete errors['date-to'];
		store.dispatch(setTooltipError(errors));
	});

export const toggleEnableReturnLocation = (toggle) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: CAR_TOGGLE_RETURN_LOCATION,
			payload: toggle,
		});
	});

export const selectFromLocation = (location) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: CAR_SELECT_FROM_LOCATION,
			payload: location,
		});
		const errors = { ...store.getState().base.tooltipErrors };
		delete errors['airport-from'];
		store.dispatch(setTooltipError(errors));
	});

export const selectToLocation = (location) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: CAR_SELECT_TO_LOCATION,
			payload: location,
		});
		const errors = { ...store.getState().base.tooltipErrors };
		delete errors['airport-to'];
		store.dispatch(setTooltipError(errors));
	});

export const doSearchCars = (compare) =>
	catchErrorSync(() => {
		compare = compare || false;
		const carState = store.getState().car;
		const baseState = store.getState().base;
		const errors = {};

		// TODO: insert error checks here
		setErrorForPriceAlertEmail(errors);
		setErrorForPriceAlertPhone(errors);

		if (!Object.keys(carState.selectedFromLocation).length) {
			errors['airport-from'] = i18n.t('validation.cars.pickUpLocation');
		}

		if (carState.enableReturnLocation) {
			if (!Object.keys(carState.selectedToLocation).length) {
				errors['airport-to'] = i18n.t('validation.cars.dropOffLocation');
			}
		}

		const { selectedFromDate, selectedToDate, selectedFromTime, selectedToTime } = carState;
		const areDatesEqual = compareAsc(startOfDay(selectedFromDate), startOfDay(selectedToDate)) === 0;
		const pickupTime = +selectedFromTime.split(':')[0];
		const dropoffTime = +selectedToTime.split(':')[0];
		const now = new Date();

		const isDropOffEarlier = areDatesEqual && dropoffTime < pickupTime;
		const isDropOffSameTime = areDatesEqual && dropoffTime === pickupTime;
		const isDropOffHourValid = areDatesEqual && dropoffTime - pickupTime < 2;

		const isPickupEarlierThanNow = isToday(selectedFromDate) && pickupTime <= now.getHours();

		if (isPickupEarlierThanNow) {
			errors['date-from'] = i18n.t('validation.cars.pickUpTime');
		}

		if (isDropOffEarlier || isDropOffSameTime || isDropOffHourValid) {
			errors['date-to'] = i18n.t('validation.cars.dropOffTime');
		}

		store.dispatch(setTooltipError(errors));
		if (
			Helper.isEmpty(errors) &&
			!baseState.isClosedPriceAlertSignUpModal &&
			baseState.priceAlertHandlerChecked &&
			Helper.isDesktop()
		) {
			store.dispatch(togglePriceAlertSignUpModal(true));
			return;
		}
		if (Helper.isEmpty(errors)) {
			store.dispatch(emitGMTEvent(store.getState().base.enabledVert));
			const parsed = Helper.parseQueryString(window.location.search, true);
			let preventOpenFrontTab = false;

			if (Helper.hasProperty(parsed, 'f') && parsed.f === '0') {
				preventOpenFrontTab = true;
			}

			if (Helper.hasProperty(parsed, 'autoredirect') && parsed.autoredirect === '1') {
				preventOpenFrontTab = true;
			}

			let openBackTabTimeout = 500;
			if (preventOpenFrontTab) {
				openBackTabTimeout = 100;
			}

			setTimeout(function () {
				saveFrontFormDataToSessionStorage(store.dispatch(getFormData('front')));
				if (compare) {
					store.dispatch(sendDataToCompare(store.dispatch(getFormData('front'))));
				} else {
					if (!preventOpenFrontTab) {
						store.dispatch(submitFormFront());
					}
					setTimeout(function () {
						store.dispatch(toggleProgressLoading(true));
						store.dispatch(submitFormBack());
					}, openBackTabTimeout);
				}
			}, 10);
		} else {
			store.dispatch(hideAndShowErrors());
		}
	});

export function getCarLocation(obj) {
	let pickupDestinationObj = obj.iata;
	if (!pickupDestinationObj) {
		pickupDestinationObj = [];
		if (obj.name) {
			pickupDestinationObj.push(obj.name);
		} else {
			pickupDestinationObj.push(obj.state);
		}
		if (obj.stateCode && obj.country === 'United States') {
			pickupDestinationObj.push(obj.stateCode);
		}
		if (obj.state && obj.country === 'United States') {
			pickupDestinationObj.push(obj.state);
		}

		if (obj.country) {
			pickupDestinationObj.push(obj.country);
		}
		pickupDestinationObj = pickupDestinationObj.join(',');
	}
	return pickupDestinationObj;
}

export const submitFormBack = () =>
	catchErrorSync(() => {
		const form = document.getElementById('redirect-back-form');
		const data = store.dispatch(getFormData('back'));
		store.dispatch(setTicketData(data));
		store.dispatch(submitForm(form, data));
	});

export const submitFormFront = () =>
	catchErrorSync(() => {
		store.dispatch(sendDataToPassthruForm(store.dispatch(getFormData('front'))));
	});

export const getFormData = (formType) =>
	catchErrorSync(() => {
		const data = store.getState().car;
		const from = data.selectedFromLocation;
		const enableReturnLocation = data.enableReturnLocation;
		const to = data.selectedToLocation;
		const dateFrom = data.selectedFromDate;
		const dateTo = data.selectedToDate;
		const fromTime = data.selectedFromTime;
		const toTime = data.selectedToTime;
		const vert = store.getState().base.enabledVert;
		const pickupDestination = getCarLocation(from);
		let pickupDestinationID = null;
		let pickupDestinationKey = null;
		if (Helper.hasProperty(from, 'airportID') || from.type === 'airport') {
			pickupDestinationID = from.iata;
			pickupDestinationKey = 'airport';
		} else if (Helper.hasProperty(from, 'cityID')) {
			pickupDestinationID = from.cityID;
			pickupDestinationKey = 'city';
		}

		// RENTAL CONTRACT DURATION
		const date_from = new Date(dateFrom);
		const date_to = new Date(dateTo);
		const diffTime = Math.abs(date_to - date_from);
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		const dta = Math.abs(date_from - Date.now());
		const dtaDiff = Math.ceil(dta / (1000 * 60 * 60 * 24));

		const fbp = getFbClientId();

		const formData = {
			tab: formType,
			vert,
			'pickup-destination': pickupDestination,
			'pickup-time': Helper.toISODateTimeString(dateFrom, fromTime),
			'drop-off-time': Helper.toISODateTimeString(dateTo, toTime),
			'pickup-t': fromTime,
			'drop-off-t': toTime,
			'pickup-destination-id': pickupDestinationID,
			'pickup-destination-key': pickupDestinationKey,
			'rental-duration': diffDays,
			dta: dtaDiff,
			...(fbp && { fbp }),
		};
		const email = store.getState().base.priceAlertEmail;
		if (email) {
			formData['email'] = email;
		}
		const phone = store.getState().base.priceAlertPhone;
		if (phone) {
			formData['phone'] = phone;
		}
		if (enableReturnLocation) {
			const dropoffDestination = getCarLocation(to);
			formData['drop-off-destination'] = dropoffDestination;
			let dropOffDestinationID = null;
			let dropOffDestinationKey = null;
			if (Helper.hasProperty(to, 'airportID')) {
				dropOffDestinationID = to.airportID;
				dropOffDestinationKey = 'airport';
			} else if (Helper.hasProperty(to, 'cityID')) {
				dropOffDestinationID = to.cityID;
				dropOffDestinationKey = 'city';
			}
			formData['drop-off-destination-id'] = dropOffDestinationID;
			formData['drop-off-destination-key'] = dropOffDestinationKey;
		}

		return formData;
	});

export const prePopulateDataFromQueryString = () =>
	catchErrorSync(() => {
		const parsed = Helper.parseQueryString(window.location.search, true);
		if (Helper.hasProperty(parsed, 'date-from') && parsed['date-from']) {
			let dateFrom = parsed['date-from'];
			if (Helper.isValidDateFormat(dateFrom)) {
				dateFrom = Helper.convertStringDateToDateObject(dateFrom);
				store.dispatch(selectFromDate(dateFrom));
			}
		}
		if (Helper.hasProperty(parsed, 'date-to') && parsed['date-to']) {
			let dateTo = parsed['date-to'];
			if (Helper.isValidDateFormat(dateTo)) {
				dateTo = Helper.convertStringDateToDateObject(dateTo);
				store.dispatch(selectToDate(dateTo));
			}
		}
		if (Helper.hasProperty(parsed, 'time-to') && parsed['time-to']) {
			const timeTo = parsed['time-to'];
			if (Helper.isValidTimeFormat(timeTo)) {
				store.dispatch(selectToTime(timeTo));
			}
		}
		if (Helper.hasProperty(parsed, 'time-from') && parsed['time-from']) {
			const timeFrom = parsed['time-from'];
			if (Helper.isValidTimeFormat(timeFrom)) {
				store.dispatch(selectFromTime(timeFrom));
			}
		}
	});
